import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Brain, ArrowLeft, AlertCircle } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, AppCard, AppCardContent, AppButton,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import AIQuestionForm from "./AIQuestionForm";
import "./AddEditAIQuestionPage.scss";

const AddEditAIQuestionPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState({ question: "", techStack: "", difficulty: "easy" });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    if (!isEdit) return;
    setFetching(true);
    apiClient
      .get(`/ai/ai-questions/${id}`)
      .then((res) => {
        setForm({
          question: res.data?.question || "",
          techStack: res.data?.techStack || "",
          difficulty: res.data?.difficulty || "easy",
        });
      })
      .catch(() => setErr("Failed to load question."))
      .finally(() => setFetching(false));
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.question.trim() || !form.techStack) {
      setErr("Please fill in the question and select a category.");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await apiClient.put(`/ai/ai-questions/${id}`, form);
        setToast({ show: true, message: "Question updated", type: "success" });
      } else {
        await apiClient.post("/ai/ai-questions", form);
        setToast({ show: true, message: "Question created", type: "success" });
      }
      // Tiny delay so the toast registers before navigation.
      setTimeout(() => navigate("/ai-bank"), 350);
    } catch (e2) {
      setErr(e2?.response?.data?.message || "Failed to save question.");
    }
    setLoading(false);
  };

  return (
    <div className="ai-edit-page">
      <PageHeader
        icon={Brain}
        backButton
        onBack={() => navigate("/ai-bank")}
        title={isEdit ? "Edit AI Question" : "New AI Question"}
        description={
          isEdit
            ? "Update an existing AI interview question."
            : "Add a new AI interview question to your bank."
        }
        actions={
          <AppButton variant="outline" onClick={() => navigate("/ai-bank")}>
            <ArrowLeft size={16} /> Back
          </AppButton>
        }
      />

      <div className="ai-edit-page__layout">
        <AppCard className="ai-edit-page__main">
          <AppCardContent>
            {err && (
              <div className="ai-edit-page__alert" role="alert">
                <AlertCircle size={16} />
                <span>{err}</span>
              </div>
            )}

            {fetching ? (
              <div className="ai-edit-page__loading">
                <div className="ai-edit-page__skeleton ai-edit-page__skeleton--lg" />
                <div className="ai-edit-page__skeleton" />
                <div className="ai-edit-page__skeleton" />
              </div>
            ) : (
              <AIQuestionForm
                form={form}
                setForm={setForm}
                loading={loading}
                onSubmit={handleSubmit}
                isEdit={isEdit}
              />
            )}
          </AppCardContent>
        </AppCard>

        <aside className="ai-edit-page__side">
          <AppCard>
            <AppCardContent>
              <h4 className="ai-edit-page__side-title">Tips</h4>
              <ul className="ai-edit-page__side-list">
                <li>Pick a focused category — students filter by it during interviews.</li>
                <li>Use the <strong>Manage categories</strong> link to pre-create new categories.</li>
                <li>Keep difficulty consistent: easy = warm-up, hard = senior-level.</li>
                <li>Need many questions? Use <strong>Import</strong> on the Questions list.</li>
              </ul>
            </AppCardContent>
          </AppCard>
        </aside>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AddEditAIQuestionPage;
